import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useProfileStore } from '../stores/useProfileStore';
import { UserSettings } from '../stores/useProfileStore';
import { useEffect } from 'react';
import { useSupabaseSubscription } from './useSupabaseSubscription';

// Query keys
export const profileKeys = {
  all: ['profile'] as const,
  settings: (userId: string) => [...profileKeys.all, 'settings', userId] as const,
};

/**
 * Hook for fetching and managing profile settings with TanStack Query
 * This provides efficient caching and data sharing between components
 */
export function useProfileSettings(userId: string | undefined) {
  const queryClient = useQueryClient();
  const setSettings = useProfileStore(state => state.setSettings);
  
  // Set up real-time subscription for profile changes only when userId is available
  useSupabaseSubscription({
    table: 'profiles',
    userId,
    queryKeys: userId ? [[...profileKeys.settings(userId)]] : [],
    filter: userId ? `id=eq.${userId}` : undefined
  });
  
  // Fetch profile settings
  const { data: settings, isLoading, error } = useQuery({
    queryKey: userId ? profileKeys.settings(userId) : ['profile', 'settings', 'undefined'],
    queryFn: async () => {
      if (!userId) return null;
      
      console.log(`[useProfileSettings] Fetching settings for user: ${userId}`);
      
      // Get profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      console.log('[useProfileSettings] Profile data loaded:', profileData);

      // Create preferences object from profile data
      const preferences = {
        theme: profileData?.theme || 'light',
        language: profileData?.language || 'en',
        currency: profileData?.currency || 'USD',
        date_format: profileData?.date_format || 'MM/DD/YYYY',
        time_format: profileData?.time_format || '12h',
        number_format: profileData?.number_format || 'thousand_dot',
        timezone: profileData?.timezone || 'UTC',
        start_of_week: profileData?.first_day_of_week || 1,
        start_of_month: profileData?.start_of_month || 1,
        fiscal_year_start: profileData?.fiscal_year_start || 1
      };

      const defaultNotifications = {
        transaction_created: true,
        transaction_deleted: true,
        large_transactions: true,
        recurring_transactions: true,
        category_changed: true,
        transfer_complete: true,
        exchange_rate_updates: true,
        new_features: true,
        app_updates: true,
        security_alerts: true,
        sync_status: true,
        budget_approaching: true,
        budget_exceeded: true,
        budget_reset: true,
        show_in_app: true,
        push_notifications: true,
        sound_enabled: true,
        vibration_enabled: true
      };

      // Merge profile data with preferences
      const settings: UserSettings = {
        full_name: profileData?.full_name || '',
        display_name: profileData?.display_name || '',
        email: profileData?.email || '',
        phone: profileData?.phone || null,
        photo_url: profileData?.photo_url || profileData?.avatar_url || null,
        preferences: preferences,
        notifications: {
          ...defaultNotifications,
          ...(profileData?.notifications || {})
        },
        created_at: profileData?.created_at || new Date().toISOString(),
        updated_at: profileData?.updated_at || new Date().toISOString()
      };

      console.log('[useProfileSettings] Mapped settings:', settings);
      
      // Update the store with the fetched settings
      setSettings(settings);
      
      return settings;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus to reduce redundant requests
    refetchOnMount: false, // Only fetch once per mount to reduce redundant requests
  });

  // Update profile settings
  const updateMutation = useMutation({
    mutationFn: async ({ userId, data }: { userId: string, data: Partial<UserSettings> }) => {
      console.log(`[useProfileSettings] Updating settings for user: ${userId}`);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: data.full_name,
          display_name: data.display_name,
          phone: data.phone,
          theme: data.preferences?.theme,
          language: data.preferences?.language,
          currency: data.preferences?.currency,
          date_format: data.preferences?.date_format,
          time_format: data.preferences?.time_format,
          number_format: data.preferences?.number_format,
          timezone: data.preferences?.timezone,
          first_day_of_week: data.preferences?.start_of_week,
          start_of_month: data.preferences?.start_of_month,
          fiscal_year_start: data.preferences?.fiscal_year_start,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
      
      return true;
    },
    onSuccess: (_, variables) => {
      // Invalidate the query to refetch the latest data
      queryClient.invalidateQueries({ queryKey: profileKeys.settings(variables.userId) });
    }
  });

  // Prefetch profile data when the component mounts
  useEffect(() => {
    if (userId) {
      queryClient.prefetchQuery({
        queryKey: profileKeys.settings(userId),
        queryFn: async () => {
          // Use the existing profile data from the store if available
          const existingSettings = useProfileStore.getState().settings;
          if (existingSettings) {
            return existingSettings;
          }
          
          // Otherwise, fetch from the API
          await useProfileStore.getState().refreshUserProfile(userId);
          return useProfileStore.getState().settings;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
      });
    }
  }, [userId, queryClient]);

  return {
    settings,
    isLoading,
    error,
    updateSettings: (data: Partial<UserSettings>) => 
      userId ? updateMutation.mutate({ userId, data }) : Promise.resolve(),
    isUpdating: updateMutation.isPending
  };
} 